import type {FieldCheckStatusRow} from "@sakura-grp/database/services";
import {useBusinessCardFieldChecks} from "@/hooks/useBusinessCardFieldChecks";
import {BusinessCardFieldMark, BusinessCardFieldReason} from "./business-card-field-status";
import {BizcardImageViewer} from "./bizcard-image-viewer";
import {useState, useEffect} from "react";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle, DrawerFooter} from "@sakura-grp/ui/drawer";
import {Button} from "@sakura-grp/ui/button";
import {Input} from "@sakura-grp/ui/input";
import {Label} from "@sakura-grp/ui/label";
import {Textarea} from "@sakura-grp/ui/textarea";
import {toast} from "@sakura-grp/ui/sonner";
import {checkBusinessCardField, updateBusinessCard, normalizeMobileInput, isValidMobileNumber, isValidEmailAddress, normalizePostalCodeInput, isValidPostalCode} from "@/lib/api/business-cards";
import {useDirtyDialogGuard} from "@/hooks/use-dirty-dialog-guard";
import {useAddressAutofill} from "@/hooks/useAddressAutofill";
import {useKeyboardInset} from "@/hooks/useKeyboardInset";
import {useKanaField} from "@/hooks/useKanaField";
import {CompanySelectSheet} from "../../shared/company-select-sheet";
import {CompanyRenameDialog} from "../../shared/company-rename-dialog";
import type {BusinessCard} from "~/types/customer-detail.types";

interface BusinessCardEditSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 編集対象の名刺（顧客詳細の transform 済みデータ） */
  card: BusinessCard;
  /** キャッシュ無効化用（顧客詳細クエリの先頭キー） */
  customerId: string;
}

export function BusinessCardEditSheet({open, onOpenChange, card, customerId}: BusinessCardEditSheetProps) {
  const [name, setName] = useState(card.name);
  const [kana, setKana] = useState(card.kana);
  const [mobile, setMobile] = useState(card.mobile);
  const [email, setEmail] = useState(card.email);
  const [company, setCompany] = useState(card.companyName);
  const [position, setPosition] = useState(card.position);
  const [companyPhone, setCompanyPhone] = useState(card.companyPhone);
  const [postalCode, setPostalCode] = useState(card.postalCode);
  const [address, setAddress] = useState(card.companyAddress);
  const [note, setNote] = useState(card.handwrittenMemo ?? "");
  const [companySheetOpen, setCompanySheetOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const queryClient = useQueryClient();
  const fieldChecks = useBusinessCardFieldChecks(open ? [String(card.id)] : []);
  const [checkRow, setCheckRow] = useState<FieldCheckStatusRow>();
  const [nameVerdict, setNameVerdict] = useState<Verdict | null>(null);
  const [addressVerdict, setAddressVerdict] = useState<Verdict | null>(null);
  const [checkNote, setCheckNote] = useState("");
  const [viewerOpen, setViewerOpen] = useState(false);
  const nameDirty = name !== card.name;
  const addressDirty = postalCode !== card.postalCode || address !== card.companyAddress;
  const hasInputChanges = name !== card.name || kana !== card.kana || mobile !== card.mobile || email !== card.email || company !== card.companyName || position !== card.position || companyPhone !== card.companyPhone || postalCode !== card.postalCode || address !== card.companyAddress || note !== (card.handwrittenMemo ?? "");
  const displayCheckRow = fieldChecks.data?.get(String(card.id)) ?? checkRow;
  const hasVerdicts = !!(nameVerdict || addressVerdict);
  useEffect(() => {
    setNameVerdict(null);
  }, [name]);
  useEffect(() => {
    setAddressVerdict(null);
  }, [postalCode, address]);
  useEffect(() => {
    if (!hasVerdicts) setCheckNote("");
  }, [hasVerdicts]);

  // 郵便番号7桁で住所を前置入力（既存住所・手入力済みは上書きしない）
  const {resetAutofill} = useAddressAutofill({postalCode, address, onFill: setAddress});

  const {error: kanaError, visibleError: kanaVisibleError, inputProps: kanaInputProps, reset: resetKanaComposition} = useKanaField(kana);

  // ドロワーを開いたとき（または対象カードが変わったとき）に現在値で初期化する。
  // card オブジェクトは毎レンダー新参照になりうるので id をキーにする（編集中の
  // 再フェッチでフォームが巻き戻らないよう、card.id 単位でのみリセット）。
  useEffect(() => {
    if (open) {
      setCheckRow(undefined);
      setNameVerdict(null);
      setAddressVerdict(null);
      setCheckNote("");
      setViewerOpen(false);
      setName(card.name);
      setKana(card.kana);
      setMobile(card.mobile);
      setEmail(card.email);
      setCompany(card.companyName);
      setPosition(card.position);
      setCompanyPhone(card.companyPhone);
      setPostalCode(card.postalCode);
      setAddress(card.companyAddress);
      setNote(card.handwrittenMemo ?? "");
      setCompanySheetOpen(false);
      setRenameOpen(false);
      resetKanaComposition();
      // 既存住所を初期表示するので、その値を「自動入力値」と誤認して上書きしないよう
      // 自動入力の履歴をクリアする（編集対象カードが変わったときも同様）
      resetAutofill();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, card.id, resetKanaComposition, resetAutofill]);

  // フォームを開いた時点の判定行を保持する。背景再取得で新しい指紋に差し替えると、
  // 他の人が変更した値を古いフォームから上書きしても BC102 にならなくなる。
  useEffect(() => {
    if (open && !checkRow && !fieldChecks.isFetching) {
      setCheckRow(fieldChecks.data?.get(String(card.id)));
    }
  }, [open, card.id, checkRow, fieldChecks.data, fieldChecks.isFetching]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!hasInputChanges && hasVerdicts && checkRow) {
        // 項目ごとに保存する。成功済みの選択は外し、途中失敗時の再送を防ぐ。
        if (nameVerdict && nameVerdict !== "corrected") {
          await checkBusinessCardField(String(card.id), "name", nameVerdict, checkRow.name_fingerprint, checkNote.trim() || undefined);
          setNameVerdict(null);
        }
        if (addressVerdict && addressVerdict !== "corrected") {
          await checkBusinessCardField(String(card.id), "address", addressVerdict, checkRow.address_fingerprint, checkNote.trim() || undefined);
          setAddressVerdict(null);
        }
        return;
      }
      return updateBusinessCard(
        String(card.id),
        {
          nameKanji: name,
          nameFurigana: kana,
          mobileNumber: mobile,
          emailAddress: email,
          companyName: company,
          position,
          companyPhone,
          address,
          postalCode,
          handwrittenNote: note,
        },
        hasVerdicts && checkRow
          ? {
              nameVerdict,
              addressVerdict,
              nameExpectedFingerprint: checkRow.name_fingerprint,
              addressExpectedFingerprint: checkRow.address_fingerprint,
              note: checkNote.trim() || undefined,
            }
          : undefined,
      );
    },
    onSuccess: () => {
      toast.success("名刺を更新しました");
      queryClient.invalidateQueries({queryKey: ["business-card-field-checks"]});
      // 詳細（自身）・一覧・件数バッジを再取得（会社名は company_id 再解決で一覧にも反映）
      queryClient.invalidateQueries({queryKey: ["customer-detail", customerId]});
      queryClient.invalidateQueries({queryKey: ["tab-customers"]});
      queryClient.invalidateQueries({queryKey: ["tab-counts"]});
      onOpenChange(false);
    },
    onError: async (error: Error & {code?: string}) => {
      if (!hasInputChanges || error.code === "BC102") {
        await Promise.all([queryClient.invalidateQueries({queryKey: ["business-card-field-checks"]}), queryClient.invalidateQueries({queryKey: ["customer-detail", customerId]})]);
      }
      if (error.code === "BC102") {
        // 古い入力を新しい指紋で確認し直さないよう、再度開いて最新値から編集する。
        onOpenChange(false);
      }
      toast.error(error.message);
    },
  });

  const normalizedMobile = normalizeMobileInput(mobile);
  const mobileError = normalizedMobile.length > 0 && !isValidMobileNumber(normalizedMobile) ? "携帯電話番号の形式が正しくありません" : null;

  const normalizedPostal = normalizePostalCodeInput(postalCode);
  const postalError = postalCode.trim().length > 0 && !isValidPostalCode(normalizedPostal) ? "郵便番号は7桁の数字で入力してください" : null;

  const emailTrimmed = email.trim();
  const emailError = emailTrimmed.length > 0 && !isValidEmailAddress(emailTrimmed) ? "メールアドレスの形式が正しくありません" : null;

  const hasChanges = hasVerdicts || hasInputChanges;

  // 印だけの保存では既存の欠損・形式を検査しない。入力を変えた保存の必須は氏名・フリガナ。連絡先等は任意だが、入れるなら正しい形式であること。変更がなければ保存不可。
  // フリガナのカタカナ検証は「フリガナを編集したとき」だけ課す（Sansan 由来等で既存値が
  // 非カタカナの名刺でも、無関係な項目だけ直したいケースを保存ブロックしない）。
  const canSave = !hasInputChanges ? hasVerdicts : name.trim().length > 0 && kana.trim().length > 0 && (kana === card.kana || !kanaError) && !mobileError && !emailError && !postalError && hasChanges;

  const {DirtyConfirmDialog, requestClose} = useDirtyDialogGuard({
    isDirty: hasChanges,
    onClose: () => onOpenChange(false),
  });

  const keyboardInset = useKeyboardInset();

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={(next) => {
          if (!next) requestClose();
        }}
        dismissible={!hasChanges && !viewerOpen}
        modal={!viewerOpen}
        repositionInputs={false}
      >
        <DrawerContent className="px-lg pb-safe-min h-[85vh]" style={keyboardInset > 0 ? {paddingBottom: keyboardInset} : undefined}>
          <DrawerHeader className="pb-md">
            <DrawerTitle>名刺を編集</DrawerTitle>
            <DrawerDescription>この名刺の情報を編集します</DrawerDescription>
          </DrawerHeader>

          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto">
            {card.front && (
              <button type="button" aria-label="名刺表面を拡大" onClick={() => setViewerOpen(true)} className="block">
                <img src={card.front} alt="名刺表面" className="max-h-36 rounded border object-contain" />
              </button>
            )}
            <div className="space-y-sm">
              <div className="flex items-center gap-2">
                <Label htmlFor="edit-card-name">氏名（必須）</Label>
                <BusinessCardFieldMark row={displayCheckRow} field="name" />
              </div>
              <BusinessCardFieldReason row={displayCheckRow} field="name" />
              <Input id="edit-card-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="山田 太郎" className="text-base" />
              <FieldVerdictControls field="name" row={checkRow && displayCheckRow} dirty={nameDirty} hasValue={!!card.name} value={nameVerdict} onChange={setNameVerdict} disabled={mutation.isPending} />
            </div>
            <div className="space-y-sm">
              <Label htmlFor="edit-card-kana">フリガナ（必須）</Label>
              <Input id="edit-card-kana" value={kana} onChange={(e) => setKana(e.target.value)} placeholder="ヤマダ タロウ" className="text-base" aria-invalid={!!kanaVisibleError} {...kanaInputProps} />
              {kanaVisibleError && (
                <p className="text-destructive text-xs" role="alert">
                  {kanaVisibleError}
                </p>
              )}
            </div>
            <div className="space-y-sm">
              <Label htmlFor="edit-card-mobile">携帯電話</Label>
              <Input id="edit-card-mobile" type="tel" inputMode="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="090-1234-5678" className="text-base" aria-invalid={!!mobileError} />
              {mobileError && (
                <p className="text-destructive text-xs" role="alert">
                  {mobileError}
                </p>
              )}
            </div>
            <div className="space-y-sm">
              <Label htmlFor="edit-card-email">メールアドレス</Label>
              <Input id="edit-card-email" type="email" inputMode="email" autoCapitalize="none" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="taro@example.com" className="text-base" aria-invalid={!!emailError} />
              {emailError && (
                <p className="text-destructive text-xs" role="alert">
                  {emailError}
                </p>
              )}
            </div>
            <div className="space-y-sm">
              <Label htmlFor="edit-card-company">会社名</Label>
              <button id="edit-card-company" type="button" onClick={() => setCompanySheetOpen(true)} className="border-input bg-input/10 gap-sm flex h-11 w-full items-center justify-between rounded-md border px-3 text-base">
                <span className={company ? "" : "text-muted-foreground/50"}>{company || "会社を選ぶ（任意）"}</span>
                <span className="text-muted-foreground shrink-0">›</span>
              </button>
              {company !== card.companyName ? (
                <p className="text-muted-foreground text-xs">この名刺だけ会社が変わります（保存で確定。元の会社は変わりません）</p>
              ) : (
                card.companyName.trim().length > 0 && (
                  <button type="button" onClick={() => setRenameOpen(true)} className="text-muted-foreground text-xs underline underline-offset-2">
                    会社名のまちがいを全体修正する
                  </button>
                )
              )}
            </div>
            <div className="space-y-sm">
              <Label htmlFor="edit-card-position">役職</Label>
              <Input id="edit-card-position" value={position} onChange={(e) => setPosition(e.target.value)} placeholder="営業部長" className="text-base" />
            </div>
            <div className="space-y-sm">
              <Label htmlFor="edit-card-company-phone">会社電話</Label>
              <Input id="edit-card-company-phone" type="tel" inputMode="tel" value={companyPhone} onChange={(e) => setCompanyPhone(e.target.value)} placeholder="03-1234-5678" className="text-base" />
            </div>
            <div className="space-y-sm">
              <Label htmlFor="edit-card-postal">郵便番号</Label>
              <Input id="edit-card-postal" inputMode="numeric" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} placeholder="1040061" className="max-w-48 text-base" aria-invalid={!!postalError} />
              {postalError && (
                <p className="text-destructive text-xs" role="alert">
                  {postalError}
                </p>
              )}
            </div>
            <div className="space-y-sm">
              <div className="flex items-center gap-2">
                <Label htmlFor="edit-card-address">住所</Label>
                <BusinessCardFieldMark row={displayCheckRow} field="address" />
              </div>
              <BusinessCardFieldReason row={displayCheckRow} field="address" />
              <Input id="edit-card-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="東京都中央区銀座1-2-3 サクラビル5F" className="text-base" />
              <FieldVerdictControls field="address" row={checkRow && displayCheckRow} dirty={addressDirty} hasValue={!!card.companyAddress} value={addressVerdict} onChange={setAddressVerdict} disabled={mutation.isPending} />
            </div>
            <div className="space-y-sm">
              <Label htmlFor="edit-card-note">手書きメモ</Label>
              <Textarea id="edit-card-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="名刺に書き込んだメモなど" rows={3} className="resize-none text-base" />
            </div>
            {hasVerdicts && (
              <div className="space-y-2">
                <Label htmlFor="edit-card-check-note">確認メモ（任意）</Label>
                <Textarea id="edit-card-check-note" value={checkNote} onChange={(e) => setCheckNote(e.target.value)} placeholder="例: 引っ越し" maxLength={200} rows={2} />
              </div>
            )}
          </div>

          <DrawerFooter className="pt-md gap-md flex shrink-0 flex-col p-0">
            {hasVerdicts && <p className="text-sm">保存すると：{[nameVerdict && `氏名 → ${verdictLabels[nameVerdict]}`, addressVerdict && `住所 → ${verdictLabels[addressVerdict]}`].filter(Boolean).join(" ／ ")}</p>}
            <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || !canSave}>
              {mutation.isPending ? "保存中..." : "保存する"}
            </Button>
            <Button variant="outline" onClick={requestClose} disabled={mutation.isPending}>
              やめる
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      <CompanySelectSheet open={companySheetOpen} onOpenChange={setCompanySheetOpen} value={company} onSelect={setCompany} />
      <CompanyRenameDialog open={renameOpen} onOpenChange={setRenameOpen} cardId={card.id} currentName={card.companyName} onRenamed={() => onOpenChange(false)} />
      {viewerOpen && <BizcardImageViewer front={card.front} back={card.back} initialSide="front" onClose={() => setViewerOpen(false)} />}
      <DirtyConfirmDialog />
    </>
  );
}

type Verdict = "confirmed" | "needs_review" | "corrected";
const verdictLabels: Record<Verdict, string> = {confirmed: "確認済み", needs_review: "あとで見る", corrected: "直して確認"};
function FieldVerdictControls({field, row, dirty, hasValue, value, onChange, disabled}: {field: "name" | "address"; row?: FieldCheckStatusRow; dirty: boolean; hasValue: boolean; value: Verdict | null; onChange: (value: Verdict | null) => void; disabled: boolean}) {
  const locked = disabled || !row?.[`${field}_fingerprint`];
  const status = row?.[`${field}_status`];
  if (dirty)
    return (
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" checked={value === "corrected"} disabled={locked} onChange={(e) => onChange(e.target.checked ? "corrected" : null)} />
        この{field === "name" ? "氏名" : "住所"}は正しい（直して確認にする）
      </label>
    );
  if (status && !["unverified", "withdrawn", "needs_review"].includes(status)) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {(["confirmed", "needs_review"] as const)
        .filter((v) => v !== "needs_review" || status !== "needs_review")
        .map((v) => (
          <button key={v} type="button" aria-pressed={value === v} disabled={locked || (v === "confirmed" && !hasValue)} onClick={() => onChange(value === v ? null : v)} className={`rounded-full border px-3 py-1 text-sm disabled:opacity-50 ${value === v ? "bg-primary text-primary-foreground" : "bg-background"}`}>
            {v === "confirmed" ? "画像と同じ" : "あとで見る"}
          </button>
        ))}
    </div>
  );
}
